import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { usePerms } from 'contexts/PermsContext';
import AlertList from 'features/Weather/features/WeatherAlerts/features/AlertList/index.web';
import { selectAlertList } from 'features/Weather/features/WeatherAlerts/features/AlertList/state/selectors/alertListSelectors';
import { setAlertList } from 'features/Weather/features/WeatherAlerts/features/AlertList/state/slices/alertListSlice';
import WeatherAlertsWidget from 'features/Weather/features/WeatherAlerts/index.web.widget';
import useLocation from 'hooks/useLocation';
import { useEffect, useState } from 'react';
import { getAlertWithLocationDetails } from 'features/Weather/features/WeatherAlerts/utils/alertLocation.utils';
import { Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetAllActiveAlertsQuery } from 'services/alerts/alerts.api';
import styles from './Alerts.module.css';

const Alerts = ({ dimensions, sidebarOpen }) => {
  const perms = usePerms();
  const { data: allActiveAlerts = [], isSuccess: didLoadActiveAlerts } =
    useGetAllActiveAlertsQuery();
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const alertList = useSelector(selectAlertList);
  const dispatch = useDispatch();
  const [fetching, setFetching] = useState(true);
  const navigate = useNavigate();

  const canDeleteAlerts = perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteAlerts']);
  useEffect(() => {
    if (!didLoadActiveAlerts) return;
    dispatch(setAlertList(allActiveAlerts));
    setFetching(false);
  }, [allActiveAlerts, didLoadActiveAlerts, dispatch]);

  useEffect(() => {
    (async () => {
      const canViewAlerts = await perms.validatePermissions(['ViewAlerts']);
      const canViewFemaAlerts = await perms.validatePermissions(['ViewFemaAlerts']);
      if (!canViewAlerts && !canViewFemaAlerts) {
        navigate('/404');
        return;
      }
    })();
  }, [navigate, perms]);

  useEffect(() => {
    // Checks if the alerts list is empty or if all alerts already have distanceToUser calculated
    if (
      !alertList.length ||
      alertList.every((alert) => alert.distanceToUser !== undefined || !effectiveLocation?.coords)
    ) {
      return;
    }

    let canceled = false; // Flag to prevent state updates if the component unmounts or dependencies change

    // Injects needed information into the alert object
    const processedAlerts = alertList.map((alert) =>
      getAlertWithLocationDetails(alert, effectiveLocation.coords),
    );
    if (!canceled) dispatch(setAlertList(processedAlerts));

    // Cleanup function ensures if dependencies change or component unmounts the async operation does not attempt to update state
    return () => {
      canceled = true;
    };
  }, [alertList, effectiveLocation, dispatch]);

  if (dimensions?.length && perms.testPermission([8, 9, 10, 11, 12, 13], ['WeatherAlerts'])) {
    return (
      <WeatherAlertsWidget
        isLoading={fetching}
        dimensions={dimensions}
        sidebarOpen={sidebarOpen}
        widgetType="list"
        route="getAllActiveAlerts"
        canDeleteAlerts={canDeleteAlerts}
      />
    );
  }

  if (
    perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewAlerts']) ||
    perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewFemaAlerts'])
  )
    return (
      <Container fluid className="d-flex h-100 min-h-0 flex-column overflow-hidden py-3">
        <div className="row h-100 min-h-0 flex-grow-1 overflow-hidden">
          <div
            className={`${styles.pageColumn} mx-auto d-flex h-100 min-h-0 flex-column overflow-hidden`}
          >
            {fetching ? (
              <WidgetLoadingAnimation />
            ) : (
              <AlertList title="Active Alerts" perms={perms} canDeleteAlerts={canDeleteAlerts} />
            )}
          </div>
        </div>
      </Container>
    );
};

export default Alerts;
